import * as XLSX from 'xlsx';
import {
  AppResourceData,
  CloudAccountExportType,
  ReportMetadata,
} from '../types';
import { DeviceType } from './constants';
import {
  asExportableCnapData,
  asExportableDevicesForAudit,
  escapeCSVValue,
} from '../../../util/export';
import { Device, CNAPCGAAudit } from '../../insights/types/InsightsTypes';
import {
  ApplicationResourcesQuery,
  CGAHierarchyAppsQuery,
} from '../CGAHierarchyAppsQuery';

/**
 * Helper to check if an error is a cancelled request
 * @param err - The error to check
 * @returns True if the request was cancelled
 */
export const isRequestCancelled = (err: unknown): boolean => {
  const anyErr = err as { name?: string; code?: string } | undefined;
  return (
    anyErr?.name === 'AbortError' ||
    anyErr?.name === 'CanceledError' ||
    anyErr?.code === 'ERR_CANCELED'
  );
};

/**
 * Generate Excel report with multiple sheets
 * @param appsData - Array of application resource data
 * @param reportMetadata - Report metadata for audit evidence
 * @param targetLogin - Target user login for filename
 * @param currentUserLogin - Current user login for author
 */
export const generateExcelReport = (
  appsData: AppResourceData[],
  reportMetadata: ReportMetadata,
  targetLogin: string,
  currentUserLogin: string,
): void => {
  const workbook = XLSX.utils.book_new();

  workbook.Props = {
    Title: `CGA Hierarchy Report for ${targetLogin}`,
    Subject: 'CGA Hierarchy Report with Resources',
    Author: currentUserLogin || 'DevHub',
    CreatedDate: new Date(),
  };

  // 1. Add Report Metadata sheet
  const applicationIdsList = reportMetadata.applicationIds.join(', ');
  const fusionQueryFormatted =
    ApplicationResourcesQuery.loc?.source?.body?.replace(/\s+/g, ' ')?.trim() ||
    '';
  const formattedCGAQuery =
    CGAHierarchyAppsQuery.loc?.source?.body?.replace(/\s+/g, ' ')?.trim() || '';
  const metadataSheet = XLSX.utils.aoa_to_sheet([
    ['CGA Hierarchy Report - Full Asset Inventory'],
    [],
    ['Report Generated At', reportMetadata.generatedAt],
    ['Generated By', reportMetadata.generatedBy],
    ['Hierarchy Level', reportMetadata.hierarchyLevel],
    ['Target User', reportMetadata.targetUser],
    ['Total Applications', reportMetadata.totalApplications.toString()],
    [],
    ['Applied Fusion Query:'],
    [reportMetadata.fusionQuery],
    [],
    ['Query Variables:'],
    ['Target Login', reportMetadata.targetUser],
    ['Hierarchy Level', reportMetadata.hierarchyLevel],
    ['Application IDs', applicationIdsList],
    [],
    ['Data Sources:'],
    ['Applications Query', formattedCGAQuery],
    ['Resources Query', fusionQueryFormatted],
  ]);
  metadataSheet['!cols'] = [{ wch: 25 }, { wch: 80 }];
  XLSX.utils.book_append_sheet(workbook, metadataSheet, 'Report Metadata');

  // 2. Add Applications Summary sheet
  const appSummaryData = appsData.map(app => ({
    'Application ID': app.appId,
    'Application Name': app.appName,
    Status: app.status,
    'App Group': app.appGroup || '',
    'Service Domain': app.serviceDomain || '',
    Owner: app.owner,
    'Physical Servers': app.devices.filter(
      d => d.type === DeviceType.PhysicalServer,
    ).length,
    'Virtual Machines': app.devices.filter(
      d => d.type === DeviceType.VirtualMachine,
    ).length,
    'Cloud Accounts': app.cloudAccounts.length,
    'CNAP Entries': app.cnapData.length,
  }));

  const appSummarySheet = XLSX.utils.json_to_sheet(appSummaryData);
  appSummarySheet['!cols'] = [
    { wch: 15 },
    { wch: 35 },
    { wch: 12 },
    { wch: 20 },
    { wch: 25 },
    { wch: 25 },
    { wch: 15 },
    { wch: 15 },
    { wch: 15 },
    { wch: 12 },
  ];
  XLSX.utils.book_append_sheet(workbook, appSummarySheet, 'Applications');

  // Collect all resources across apps
  const allPhysicalServers: Device[] = [];
  const allVirtualMachines: Device[] = [];
  const allCloudAccounts: CloudAccountExportType[] = [];
  const allCnapData: CNAPCGAAudit[] = [];

  appsData.forEach(appData => {
    allPhysicalServers.push(
      ...appData.devices.filter(d => d.type === DeviceType.PhysicalServer),
    );
    allVirtualMachines.push(
      ...appData.devices.filter(d => d.type === DeviceType.VirtualMachine),
    );
    allCloudAccounts.push(...appData.cloudAccounts);
    allCnapData.push(...appData.cnapData);
  });

  // 3. Add OnPrem Servers sheet
  const physicalServerData =
    allPhysicalServers.length > 0
      ? asExportableDevicesForAudit(allPhysicalServers)
      : [{ 'OnPrem Servers': 'No data available' }];
  const physicalSheet = XLSX.utils.json_to_sheet(physicalServerData);
  physicalSheet['!cols'] = Array(
    Object.keys(physicalServerData[0]).length,
  ).fill({ wch: 20 });
  XLSX.utils.book_append_sheet(workbook, physicalSheet, 'OnPrem Servers');

  // 4. Add Private Cloud Servers (VMs) sheet
  const vmData =
    allVirtualMachines.length > 0
      ? asExportableDevicesForAudit(allVirtualMachines)
      : [{ 'Private Cloud Servers': 'No data available' }];
  const vmSheet = XLSX.utils.json_to_sheet(vmData);
  vmSheet['!cols'] = Array(Object.keys(vmData[0]).length).fill({ wch: 20 });
  XLSX.utils.book_append_sheet(workbook, vmSheet, 'Private Cloud Servers');

  // 5. Add Cloud Accounts sheet
  const cloudData =
    allCloudAccounts.length > 0
      ? allCloudAccounts
      : [{ 'Cloud Accounts': 'No data available' }];
  const cloudSheet = XLSX.utils.json_to_sheet(cloudData);
  cloudSheet['!cols'] = Array(Object.keys(cloudData[0]).length).fill({
    wch: 25,
  });
  XLSX.utils.book_append_sheet(workbook, cloudSheet, 'Cloud Accounts');

  // 6. Add CNAP Report sheet
  const cnapExportData =
    allCnapData.length > 0
      ? asExportableCnapData(allCnapData)
      : [{ 'CNAP Report': 'No data available' }];
  const cnapSheet = XLSX.utils.json_to_sheet(cnapExportData);
  cnapSheet['!cols'] = Array(Object.keys(cnapExportData[0]).length).fill({
    wch: 20,
  });
  XLSX.utils.book_append_sheet(workbook, cnapSheet, 'CNAP Report');

  // Generate and download
  const excelBuffer = XLSX.write(workbook, {
    bookType: 'xlsx',
    type: 'array',
  });
  const excelBlob = new Blob([excelBuffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const excelUrl = URL.createObjectURL(excelBlob);
  const a = document.createElement('a');
  a.href = excelUrl;
  a.download = `CGA_Hierarchy_Report_${targetLogin}_${new Date().toISOString().split('T')[0]}.xlsx`;
  a.click();
  URL.revokeObjectURL(excelUrl);
};

/**
 * Generate CSV report
 * @param appsData - Array of application resource data
 * @param reportMetadata - Report metadata for audit evidence
 * @param targetLogin - Target user login for filename
 */
export const generateCSVReport = (
  appsData: AppResourceData[],
  reportMetadata: ReportMetadata,
  targetLogin: string,
): void => {
  const csvData: string[][] = [
    ['CGA Hierarchy Report - Full Asset Inventory'],
    [],
    ['Report Metadata'],
    ['Report Generated At', escapeCSVValue(reportMetadata.generatedAt)],
    ['Generated By', escapeCSVValue(reportMetadata.generatedBy)],
    ['Hierarchy Level', escapeCSVValue(reportMetadata.hierarchyLevel)],
    ['Target User', escapeCSVValue(reportMetadata.targetUser)],
    ['Total Applications', reportMetadata.totalApplications.toString()],
    [],
  ];

  // Add Applications Summary
  csvData.push(['Applications Summary']);
  csvData.push([
    'Application ID',
    'Application Name',
    'Status',
    'App Group',
    'Service Domain',
    'Owner',
    'Physical Servers',
    'Virtual Machines',
    'Cloud Accounts',
    'CNAP Entries',
  ]);

  appsData.forEach(app => {
    csvData.push([
      app.appId.toString(),
      escapeCSVValue(app.appName),
      app.status,
      escapeCSVValue(app.appGroup || ''),
      escapeCSVValue(app.serviceDomain || ''),
      escapeCSVValue(app.owner),
      app.devices
        .filter(d => d.type === DeviceType.PhysicalServer)
        .length.toString(),
      app.devices
        .filter(d => d.type === DeviceType.VirtualMachine)
        .length.toString(),
      app.cloudAccounts.length.toString(),
      app.cnapData.length.toString(),
    ]);
  });
  csvData.push([]);

  // Add each app's detailed resources
  appsData.forEach(appData => {
    const physicalServers = appData.devices.filter(
      d => d.type === DeviceType.PhysicalServer,
    );
    const virtualMachines = appData.devices.filter(
      d => d.type === DeviceType.VirtualMachine,
    );

    csvData.push([
      `=== Application: ${appData.appName} (ID: ${appData.appId}) ===`,
    ]);
    csvData.push([`Owner: ${appData.owner}`]);
    csvData.push([]);

    // OnPrem Servers
    csvData.push(['OnPrem Servers']);
    if (physicalServers.length > 0) {
      const exportableDevices = asExportableDevicesForAudit(physicalServers);
      const headers = Object.keys(exportableDevices[0]);
      csvData.push(headers);
      exportableDevices.forEach(device => {
        csvData.push(headers.map(header => escapeCSVValue(device[header])));
      });
    } else {
      csvData.push(['No data available']);
    }
    csvData.push([]);

    // Virtual Machines
    csvData.push(['Private Cloud Servers']);
    if (virtualMachines.length > 0) {
      const exportableDevices = asExportableDevicesForAudit(virtualMachines);
      const headers = Object.keys(exportableDevices[0]);
      csvData.push(headers);
      exportableDevices.forEach(device => {
        csvData.push(headers.map(header => escapeCSVValue(device[header])));
      });
    } else {
      csvData.push(['No data available']);
    }
    csvData.push([]);

    // Cloud Accounts
    csvData.push(['Cloud Accounts']);
    if (appData.cloudAccounts.length > 0) {
      csvData.push([
        'Account Name',
        'Account ID',
        'Platform',
        'Environment',
        'Technical Owner',
        'Associated Applications',
      ]);
      appData.cloudAccounts.forEach(account => {
        csvData.push([
          escapeCSVValue(account['Account name']),
          account['Account ID'],
          account.Platform,
          account.Environment,
          escapeCSVValue(account['Technical owner']),
          escapeCSVValue(account['Associated applications']),
        ]);
      });
    } else {
      csvData.push(['No data available']);
    }
    csvData.push([]);

    // CNAP Data
    csvData.push(['CNAP Report']);
    if (appData.cnapData.length > 0) {
      csvData.push([
        'DevHub App Name',
        'Application ID',
        'App Route',
        'App Name',
        'Subnet',
        'Platform',
      ]);
      appData.cnapData.forEach(cnap => {
        csvData.push([
          escapeCSVValue(cnap.devhubAppName),
          cnap.applicationId.toString(),
          escapeCSVValue(cnap.appRoute),
          escapeCSVValue(cnap.appName),
          escapeCSVValue(cnap.subnet),
          cnap.platform,
        ]);
      });
    } else {
      csvData.push(['No data available']);
    }
    csvData.push([]);
    csvData.push([]);
  });

  // Add data source links
  csvData.push(['Data Sources']);
  csvData.push(['Query Name', 'Description']);
  csvData.push(['CGAHierarchyAppsQuery', 'Fetch applications from portfolio']);
  csvData.push([
    'ApplicationResourcesQuery',
    'fetch resources for applications',
  ]);
  csvData.push([]);
  csvData.push(['Applied Fusion Query:']);
  csvData.push([
    escapeCSVValue(reportMetadata.fusionQuery.replace(/\n/g, ' ')),
  ]);
  csvData.push([]);
  csvData.push(['Query Variables:']);
  csvData.push(['Target Login', escapeCSVValue(reportMetadata.targetUser)]);
  csvData.push([
    'Hierarchy Level',
    escapeCSVValue(reportMetadata.hierarchyLevel),
  ]);
  csvData.push([
    'Application IDs',
    escapeCSVValue(reportMetadata.applicationIds.join('; ')),
  ]);

  // Use Blob and URL.createObjectURL for better handling of large files and special characters
  const csvContent = csvData.map(e => e.join(',')).join('\n');
  const csvBlob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const csvUrl = URL.createObjectURL(csvBlob);

  const link = document.createElement('a');
  link.href = csvUrl;
  link.download = `CGA_Hierarchy_Report_${targetLogin}_${new Date().toISOString().split('T')[0]}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(csvUrl);
};
